import * as d3 from 'd3';
import { numberToLocaleString } from '@/lib/utils';


//Helpers
const translate = (x: number, y: number) => `translate(${x}, ${y})`;

export type NewPlayersDatum = {
    date: Date;
    count: number;
};

type DrawNewPlayersProps = {
    svgRef: SVGElement;
    setRenderError: (error: string) => void;
    size: {
        width: number;
        height: number;
    };
    margins: {
        top: number;
        right: number;
        bottom: number;
        left: number;
        axis: number;
    };
    domain: [Date, Date];
    bucketMs: number;
    data: NewPlayersDatum[];
    onHover: (datum: NewPlayersDatum | undefined) => void;
};

export default function drawNewPlayers({
    svgRef,
    setRenderError,
    size: { width, height },
    margins,
    domain,
    bucketMs,
    data,
    onHover,
}: DrawNewPlayersProps) {
    //Clear
    const svg = d3.select(svgRef);
    svg.selectAll('*').remove();
    if (!width || !height || !data.length) return;

    try {
        const drawableAreaHeight = height - margins.top - margins.bottom;
        const drawableAreaWidth = width - margins.left - margins.right;
        if (drawableAreaHeight < 20 || drawableAreaWidth < 40) return;

        const chartGroup = svg.append('g')
            .attr('transform', translate(margins.left, margins.top));

        //every bucket gets an equal slot and sits in the middle of it, so the first
        //and last one stay inside the drawable area instead of bleeding into the margins
        const bucketCount = Math.max(1, Math.round((domain[1].getTime() - domain[0].getTime()) / bucketMs) + 1);
        const bucketWidth = drawableAreaWidth / bucketCount;

        //Scales
        const timeScale = d3.scaleTime()
            .domain(domain)
            .range([bucketWidth / 2, drawableAreaWidth - bucketWidth / 2]);
        const observedMax = d3.max(data, (d) => d.count) ?? 0;
        const countScale = d3.scaleLinear()
            .domain([0, Math.max(observedMax, 1)])
            .nice()
            .range([drawableAreaHeight, 0]);

        //Axis - no ticks or baselines, the dashed grid carries the reading
        const countTickCount = height > 220 ? 6 : 4;
        const timeAxisTicksScale = d3.scaleLinear([382, 1350], [4, 10]);
        const timeAxis = d3.axisBottom(timeScale)
            .ticks(Math.round(timeAxisTicksScale(width)))
            .tickSize(0)
            .tickPadding(8);
        chartGroup.append('g')
            .attr('transform', translate(0, drawableAreaHeight))
            .attr('class', 'time-axis')
            .call(timeAxis)
            .call((g) => g.select('.domain').remove());

        //these are player counts, so fractional ticks would just repeat the same label
        const countScaleMax = countScale.domain()[1];
        const countTicks = countScaleMax <= 7
            ? d3.range(countScaleMax + 1)
            : countScale.ticks(countTickCount);
        const countAxis = d3.axisLeft(countScale)
            .tickValues(countTicks)
            .tickSize(0)
            .tickPadding(8)
            .tickFormat((d) => numberToLocaleString(d as number));
        svg.append('g')
            .attr('class', 'counts-axis')
            .attr('transform', translate(margins.left - margins.axis, margins.top))
            .call(countAxis)
            .call((g) => g.select('.domain').remove());

        //Horizontal grid, same dashed hairlines as the sanction stats chart
        chartGroup.append('g')
            .selectAll('line')
            .data(countTicks)
            .join('line')
            .attr('class', 'stroke-edge')
            .attr('stroke-dasharray', '8 6')
            .attr('x1', 0)
            .attr('x2', drawableAreaWidth)
            .attr('y1', (d) => Math.round(countScale(d)) + 0.5)
            .attr('y2', (d) => Math.round(countScale(d)) + 0.5);

        //One bar per bucket, the empty ones simply have no height
        const barWidth = Math.max(1, bucketWidth - 1);
        chartGroup.append('g')
            .selectAll('rect')
            .data(data)
            .join('rect')
            .attr('class', 'fill-accent')
            .attr('x', (d) => timeScale(d.date) - barWidth / 2)
            .attr('y', (d) => countScale(d.count))
            .attr('width', barWidth)
            .attr('height', (d) => drawableAreaHeight - countScale(d.count));

        //Hover readout
        const cursorGroup = chartGroup.append('g').attr('opacity', 0);
        cursorGroup.append('rect')
            .attr('class', 'interaction-highlight')
            .attr('y', 0)
            .attr('width', Math.max(2, bucketWidth))
            .attr('height', drawableAreaHeight);
        cursorGroup.append('line')
            .attr('class', 'stroke-edge-strong')
            .attr('y1', 0)
            .attr('y2', drawableAreaHeight);

        const bisectDate = d3.bisector<NewPlayersDatum, Date>((d) => d.date).center;
        chartGroup.append('rect')
            .attr('width', drawableAreaWidth)
            .attr('height', drawableAreaHeight)
            .attr('fill', 'transparent')
            .attr('cursor', 'crosshair')
            .on('mousemove', (event) => {
                const [pointerX] = d3.pointer(event);
                const datum = data[bisectDate(data, timeScale.invert(pointerX))];
                if (!datum) return;
                const x = timeScale(datum.date);
                cursorGroup.attr('opacity', 1)
                    .select('line').attr('x1', x).attr('x2', x);
                cursorGroup.select('rect')
                    .attr('x', x - Math.max(2, bucketWidth) / 2);
                onHover(datum);
            })
            .on('mouseleave', () => {
                cursorGroup.attr('opacity', 0);
                onHover(undefined);
            });
    } catch (error) {
        setRenderError((error as Error).message ?? 'unknown error');
    }
}
